import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Megaphone, Plus, Save, Trash2 } from 'lucide-react';
import { Button, Checkbox, DataTable, DateField, EmptyState, ErrorState, FormField, FormGrid, Modal, NumberField, PageHeader, StatusBadge, TextField, Textarea, useConfirm, useToast, type Column } from '@/components';
import { errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { formatDate } from '@/lib/format';
import { useAnnouncements, useDeleteAnnouncement, useSaveAnnouncement, type AnnouncementDto } from './api';

const schema = z
  .object({
    title: z.string().trim().min(1, 'タイトルを入力してください').max(200, '200文字以内で入力してください'),
    body: z.string().max(10000, '10000文字以内で入力してください'),
    url: z
      .string()
      .trim()
      .max(500, '500文字以内で入力してください')
      .refine((v) => v === '' || /^(https?:\/\/|\/)/.test(v), 'リンクURLは http:// https:// または / から始めてください'),
    isVisible: z.boolean(),
    displayOrder: z.number().int().min(0).max(99999).nullable(),
    publishFrom: z.string(),
    publishTo: z.string(),
  })
  .refine((v) => !v.publishFrom || !v.publishTo || v.publishFrom <= v.publishTo, { path: ['publishTo'], message: '掲載期間に誤りがあります' });
type Values = z.infer<typeof schema>;

const toValues = (a?: AnnouncementDto): Values => ({
  title: a?.title ?? '',
  body: a?.body ?? '',
  url: a?.url ?? '',
  isVisible: a?.isVisible ?? true,
  displayOrder: a?.displayOrder ?? 0,
  publishFrom: a?.publishFrom ?? '',
  publishTo: a?.publishTo ?? '',
});

/** S-93 お知らせ管理（基金のみ） */
export function AnnouncementsPage() {
  const list = useAnnouncements();
  const [editing, setEditing] = useState<AnnouncementDto | 'new' | null>(null);

  const columns: Column<AnnouncementDto>[] = [
    { key: 'displayOrder', header: '表示順', numeric: true, sortable: true, width: 90 },
    { key: 'title', header: 'タイトル', sortable: true, render: (a) => <strong>{a.title}</strong> },
    { key: 'isVisible', header: '表示', width: 100, align: 'center', sortable: true, render: (a) => (a.isVisible ? <StatusBadge tone="success" dot>表示</StatusBadge> : <StatusBadge dot>非表示</StatusBadge>) },
    { key: 'publishFrom', header: '掲載期間', width: 250, render: (a) => (a.publishFrom || a.publishTo ? <span className="bs-num">{formatDate(a.publishFrom) || '—'} 〜 {formatDate(a.publishTo) || '—'}</span> : '常時') },
    { key: 'url', header: 'リンク', width: 220, render: (a) => (a.url ? <span className="bs-num" style={{ fontSize: 12 }}>{a.url}</span> : '—') },
  ];

  return (
    <>
      <PageHeader
        eyebrow="システム管理"
        title="お知らせ管理"
        description="ポータルに表示するお知らせを管理します。表示にチェックがあり、掲載期間内のものだけが表示されます。"
        actions={
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>
            新規登録
          </Button>
        }
      />
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <DataTable
          caption="お知らせ一覧"
          columns={columns}
          rows={list.data ?? []}
          rowKey={(a) => a.id}
          loading={list.isPending}
          defaultSort={{ key: 'displayOrder', direction: 'asc' }}
          dense
          onRowClick={(a) => setEditing(a)}
          rowClassName={(a) => (a.isVisible ? undefined : 'is-muted')}
          empty={<EmptyState compact icon={<Megaphone />} title="お知らせはありません" description="「新規登録」から追加してください。" />}
        />
      )}
      {editing && <AnnouncementDialog key={editing === 'new' ? 'new' : editing.id} target={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function AnnouncementDialog({ target, onClose }: { target?: AnnouncementDto; onClose: () => void }) {
  const isNew = !target;
  const toast = useToast();
  const confirm = useConfirm();
  const save = useSaveAnnouncement(target?.id ?? null);
  const del = useDeleteAnnouncement();
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: toValues(target) });

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ ...v, body: v.body || null, url: v.url || null, publishFrom: v.publishFrom || null, publishTo: v.publishTo || null, displayOrder: v.displayOrder ?? 0 });
      toast.success(isNew ? 'お知らせを登録しました' : 'お知らせを更新しました');
      onClose();
    } catch (e) {
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });

  const onDelete = async () => {
    if (!target) return;
    if (!(await confirm({ title: 'お知らせを削除しますか？', message: `「${target.title}」を削除します。この操作は取り消せません。`, tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await del.mutateAsync(target.id);
      toast.success('お知らせを削除しました');
      onClose();
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isNew ? 'お知らせの新規登録' : 'お知らせの編集'}
      footer={
        <>
          {!isNew && (
            <Button variant="danger" icon={<Trash2 size={16} />} onClick={onDelete} loading={del.isPending} style={{ marginRight: 'auto' }}>
              削除
            </Button>
          )}
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="primary" icon={<Save size={16} />} onClick={() => void onSubmit()} loading={isSubmitting}>
            {isNew ? '登録する' : '保存する'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <FormGrid columns={2}>
          <FormField label="タイトル" required span={2} error={errors.title?.message}>
            <TextField data-autofocus {...register('title')} />
          </FormField>
          <FormField label="本文" span={2} error={errors.body?.message}>
            <Textarea rows={5} {...register('body')} />
          </FormField>
          <FormField label="リンクURL" span={2} hint="https://… または /api/manuals/latest のような相対パス" error={errors.url?.message}>
            <TextField {...register('url')} />
          </FormField>
          <FormField label="掲載開始日" error={errors.publishFrom?.message}>
            <DateField showWareki {...register('publishFrom')} />
          </FormField>
          <FormField label="掲載終了日" error={errors.publishTo?.message}>
            <DateField showWareki {...register('publishTo')} />
          </FormField>
          <FormField label="表示順" hint="小さい順に表示" error={errors.displayOrder?.message}>
            <Controller control={control} name="displayOrder" render={({ field }) => <NumberField {...field} width="sm" />} />
          </FormField>
          <FormField label="表示">
            <Controller control={control} name="isVisible" render={({ field }) => <Checkbox label="ポータルに表示する" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
          </FormField>
        </FormGrid>
      </form>
    </Modal>
  );
}
